import React from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";

import { Audience, AudienceComparison, Insight, PenPortraitSection } from "@/types";
import { AudienceMiniStats, CompareMap, DemographicsSection } from "@/components/molecules";
import { PenPortraitCompareCard, TopMindsetCompareCard } from "@/components/atoms";


interface CompareInsightProps {
  index: number;
  loading: boolean;
  audience: Audience;
  insight?: Insight;
  score: string;
  sections: PenPortraitSection[];
  comparison?: AudienceComparison
  color?: string;
  gradient?: string[];
}

const CompareInsightView: React.FC<CompareInsightProps> = ({
  index,
  audience,
  loading,
  insight,
  score,
  sections,
  comparison,
  color,
  gradient
}) => {

  const theme = useTheme();

  return (
    <Grid size={6}>
      <Box
        display="flex"
        justifyContent="center"
        sx={{
          minHeight: "60vh",
          padding: 2,
          borderRadius: 2,
          borderStyle: "solid",
          borderWidth: "2px",
          borderColor: color,
        }}
      >
      {loading ?
        <CircularProgress color="inherit" />
      :
        <Grid
          size={12}
          container
          rowSpacing={2}
          columnSpacing={2}
          flexDirection="column"
          alignItems="center"
          justifyContent="flex-start"
        >
          <Grid size={12}>
            <Typography sx={{ textAlign: "center" }}>
              {audience.audience_name}
            </Typography>
          </Grid>

          {/* Audience Size section */}
          {audience.audience_size && (
            <Grid size={12}>
              <AudienceMiniStats
                audienceSize={audience.audience_size[0]}
                audienceGrowth={(index === 1 && comparison) ? comparison.audience_size : undefined}
                size="small"
                color="inherit"
                shrink={true}
                justifyContent="center"
              />
            </Grid>
          )}

          {insight && (
            <Grid size={12}>

              {/* Demographics Section */}
              <Accordion
                defaultExpanded
                sx={{
                  border: 1,
                  borderRadius: 1,
                  borderColor: theme.palette.divider,
                  mb: 2
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="body2">
                    Who are they?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid
                    container
                    size={12}
                    flexDirection={{
                      sm: "column",
                      md: "column",
                      lg: "column",
                      xl: "row"
                    }}
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                  >
                    <DemographicsSection
                      insights={insight?.insights[0]}
                      score={score}
                      compareMode={true}
                      gradient={gradient}
                    />
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Map Section */}
              <Accordion
                defaultExpanded
                sx={{
                  border: 1,
                  borderRadius: 1,
                  borderColor: theme.palette.divider,
                  mb: 2
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="body2">
                    Where can I find them?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <CompareMap
                    scores={insight.locations[0].area.slice(5)}
                    overlap={comparison ? comparison.locations[index] : []}
                    gradient={gradient}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Pen Portrait Section */}
              <Grid
                size={12}
                container
                rowSpacing={2}
                columnSpacing={2}
                flexDirection="column"
                alignItems="center"
                justifyContent="flex-start"
              >
                {sections.map((section, sectionIdx) => {
                  if (section.title === 'mindset' && insight.mindsets) {
                    return (
                      <Grid size={12} key={`pen-portrait-mindsets`}>
                        <TopMindsetCompareCard
                          title={'What are their mindsets?'}
                          variables={insight.mindsets[0]}
                          overlapping={comparison?.mindsets}
                          score={score}
                          editable={false}
                        />
                      </Grid>
                    )
                  } else {
                    const variables = insight.pen_portraits && insight.pen_portraits[0].insight.find(
                      (v) => v.section === section.title
                    )?.variables;

                    const sectionComparison = comparison?.pen_portrait?.find(
                      (v) =>  v.section.title === section.title
                    );

                    if (variables) {
                      return (
                        <Grid size={12} key={`pen-portrait-${sectionIdx}`}>
                          <PenPortraitCompareCard
                            title={section.title}
                            variables={variables}
                            comparison={sectionComparison}
                            score={score}
                            showSectionComparison={index === 1}
                          />
                        </Grid>
                      )

                    }
                    else {
                      return null;
                    }

                  }
                })}
              </Grid>



            </Grid>
          )}

        </Grid>
      }
      </Box>
    </Grid>
  );
};

export default CompareInsightView;
